export type AsterContext =
  | "homepage"
  | "onboarding"
  | "atlas"
  | "course"
  | "reflection"
  | "sky"
  | "program"
  | "ceremony"
  | "celebration";

export type AsterSettings = {
  enabled: boolean;
  greeting: string;
  homepage_title: string;
  homepage_message: string;
  homepage_sayings: string[];
  seasonal_message: string;
  intro_welcome_title: string;
  intro_welcome_body: string;
  intro_story_title: string;
  intro_story_body: string;
  intro_ignition_title: string;
  intro_ignition_body: string;
  intro_journey_title: string;
  intro_journey_body: string;
  course_introduction: string;
  lesson_why_it_matters: string;
  lesson_real_life_connection: string;
  lesson_summary: string;
  reflection_prompt: string;
  first_course_celebration: string;
  first_star_celebration: string;
  constellation_celebration: string;
  anniversary_celebration: string;
  foundation_achievement: string;
  mission_text: string;
  current_initiative_message: string;
  voice_enabled: boolean;
  voice_style: string;
  animation_frequency: "none" | "gentle" | "full";
  appearance_frequency: "important_only" | "once_per_session" | "always";
  display_seconds: number;
};

export const defaultAsterSettings: AsterSettings = {
  enabled: true,
  greeting: "Welcome. I’m Aster, the Foundation Star Guide.",
  homepage_title: "A Steady Light for the Journey Ahead.",
  homepage_message:
    "The Guided Pathway Foundation helps people strengthen emotional intelligence, resilience, and connection. I’ll point out a few places where you can begin exploring.",
  homepage_sayings: [
    "Every light begins by helping another.",
    "Learning changes what we know. Service changes what we leave behind.",
    "You do not have to light the whole sky. Begin with the next kind action.",
    "Reflection helps experience become wisdom.",
    "Connection grows when we listen with care.",
    "Your Atlas records contribution, never personal worth.",
    "Small acts can become lasting guide-lights.",
    "Hope becomes visible when people choose to show up for one another.",
  ],
  seasonal_message: "",
  intro_welcome_title: "Welcome to the Foundation Sky.",
  intro_welcome_body:
    "My name is Aster. I am the Foundation Star Guide, and I’ll be walking beside you throughout your journey.\n\nYour Atlas is where learning, reflection, service, and recognition come together as one private record of your path with The Guided Pathway Foundation.",
  intro_story_title: "Every light begins by helping another.",
  intro_story_body:
    "The first Guiding Star was ignited by an act of service so meaningful that its light became part of a shared sky. Every verified contribution since then has added to that story.\n\nA Star is not a score, rank, or measure of personal worth. It honors a real contribution and the positive path it helped illuminate for others.",
  intro_ignition_title: "Service becomes part of your Atlas through care and verification.",
  intro_ignition_body:
    "Choose an available Star, complete the contribution, and document what you did. An authorized Foundation reviewer confirms the record before the Star is formally ignited in your sky.",
  intro_journey_title: "Learn. Serve. Reflect. Let your light become part of something larger.",
  intro_journey_body:
    "Begin with Star University, explore current programs and events, then visit the Star Library when you are ready to understand the contributions you can pursue.\n\nI’ll appear at meaningful moments with short, approved guidance—but your choices, service, and story will always remain your own.",
  course_introduction:
    "We learn so we can understand ourselves, communicate with care, and serve our communities with greater awareness.",
  lesson_why_it_matters:
    "This lesson strengthens the understanding you use to communicate with care, make responsible choices, and support the Foundation mission.",
  lesson_real_life_connection:
    "Look for one practical place to use this idea—in a conversation, a team decision, a moment of service, or the way you respond under pressure.",
  lesson_summary:
    "Carry one idea from this lesson into your next conversation, decision, or act of service.",
  reflection_prompt:
    "Take a quiet moment to notice what changed, what you learned, and what you want to carry forward.",
  first_course_celebration:
    "You completed your first Foundation course. The knowledge matters most in how gently and thoughtfully you put it into practice.",
  first_star_celebration:
    "Your first Star has been ignited. It records a verified contribution—and the light it left in our shared work.",
  constellation_celebration:
    "A constellation in your Atlas is now complete. Each Star remembers a different contribution, and together they reveal a larger story of service.",
  anniversary_celebration:
    "Another year of your Foundation journey is part of the record. Thank you for continuing to learn, connect, and serve.",
  foundation_achievement:
    "The Foundation reached a new milestone because people chose to show up for one another.",
  mission_text:
    "Strengthen emotional intelligence, build resilience, support healthy relationships, and help prevent emotional crises through education, connection, and community.",
  current_initiative_message: "",
  voice_enabled: false,
  voice_style: "calm, warm, patient, and concise",
  animation_frequency: "gentle",
  appearance_frequency: "once_per_session",
  display_seconds: 16,
};
